import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {IFaehigkeit} from '../../../../shared/model/faehigkeit.model';
import {MatButton} from '@angular/material/button';
import {TranslatePipe} from '@ngx-translate/core';
import {FormsModule} from '@angular/forms';
import {MatChip, MatChipSet} from '@angular/material/chips';
import {RequestDialogComponent} from '../../../../shared/components/request-dialog/request-dialog.component';
import {MatIcon} from '@angular/material/icon';
import {Requests, RequestType} from '../../../../shared/model/request.model';
import {WikiPreviewDirective} from '../../../../shared/wiki-preview.directive';
import {CiMultiselectComponent} from '../../../../../profiler-theme';
import {MessageService} from '../../../../shared/components/message.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {VocabularyService} from '../../../../services/vocabulary.service';
import {VocabularyPipe} from '../../../../shared/vocabulary.pipe';

interface GroupedSkills {
  kategorie: string;
  skills: IFaehigkeit[];
}

@Component({
  selector: 'app-skill-select',
  templateUrl: './skill-select.component.html',
  imports: [
    MatButton,
    TranslatePipe,
    FormsModule,
    MatChipSet,
    MatChip,
    RequestDialogComponent,
    MatIcon,
    WikiPreviewDirective,
    CiMultiselectComponent,
    VocabularyPipe,
  ],
  styleUrls: ['./skill-select.component.scss'],
  standalone: true
})
export class SkillSelectComponent implements OnInit, OnChanges {
  @Input() beraterSkills: IFaehigkeit[];
  @Input() editMode: boolean;
  @Input() skillList: IFaehigkeit[];
  @Input() searchLabel: string;
  // Steuert das Rendern der schweren Auswahlliste; bei Aufgaben nur fürs aufgeklappte Panel true.
  @Input() active = true;

  @Output() changedBeraterSkills = new EventEmitter<IFaehigkeit[]>();
  @Output() skillSaved = new EventEmitter<void>();

  optionSkills: IFaehigkeit[] = [];
  groupedBeraterSkills: GroupedSkills[] = [];
  protected get RequestType() { return RequestType; }
  showModalAddRequest = false;

  constructor(
    protected http: HttpClient,
    private messageService: MessageService,
    private vocabulary: VocabularyService,
  ) {
    this.vocabulary.changes$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.displaySkill = skill => this.vocabulary.skillLabel(skill);
      this.refreshOptions();
      this.groupedBeraterSkills = this.groupSkills(this.beraterSkills);
    });
  }

  /** Neu zugewiesen bei jedem Sprachwechsel, damit die OnPush-Auswahlliste neu zeichnet. */
  displaySkill = (skill: IFaehigkeit): string => this.vocabulary.skillLabel(skill);

  /**
   * Die id entscheidet nur, wenn beide Seiten eine tragen. Ueber den CV-Import kommen
   * Skills ohne id herein: gegen den Katalogeintrag verglichen galten sie sonst als
   * ungewaehlt, und ein Klick legte denselben Skill ein zweites Mal an.
   */
  compareSkill = (a: IFaehigkeit, b: IFaehigkeit): boolean =>
    a?.id && b?.id
      ? a.id === b.id
      : a?.kategorie === b?.kategorie && a?.faehigkeit === b?.faehigkeit;

  ngOnInit() {
    this.refreshOptions();
    this.groupedBeraterSkills = this.groupSkills(this.beraterSkills);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['skillList']) {
      this.refreshOptions();
    }
    if (changes['beraterSkills']) {
      this.groupedBeraterSkills = this.groupSkills(this.beraterSkills);
    }
  }

  onSkillsSelected(skills: IFaehigkeit[]) {
    this.changedBeraterSkills.emit([...skills]);
  }

  private refreshOptions() {
    this.optionSkills = this.groupSkills(this.skillList).flatMap((group) => group.skills);
  }

  onSkillSaved() {
    this.skillSaved.emit();
  }

  private groupSkills(skills: IFaehigkeit[]): GroupedSkills[] {
    const groupedMap: Map<string, IFaehigkeit[]> = new Map();
    for (const skill of skills ?? []) {
      const kategorie = skill?.kategorie ?? '';
      if (!groupedMap.has(kategorie)) {
        groupedMap.set(kategorie, []);
      }
      groupedMap.get(kategorie)!.push(skill);
    }

    const groupedResult: GroupedSkills[] = Array.from(groupedMap.values()).map(
      (skills) => ({
        kategorie: this.vocabulary.skillCategory(skills[0]),
        skills: this.vocabulary.sort(skills, skill => this.vocabulary.skillName(skill))
      })
    );

    return this.vocabulary.sort(groupedResult, group => group.kategorie);
  }

  onAddRequestClick() {
    this.showModalAddRequest = true;
  }

  /** Der Dublettentreffer ist schon freigegeben und wird statt einer neuen Anfrage ausgewählt. */
  onExistingChosen(existing: Requests) {
    const skill = this.skillList?.find(s => s.id === existing.id);
    if (!skill) {
      // Die Fähigkeitenliste wird einmal pro Seitenaufruf geladen; eine spätere Freigabe fehlt darin.
      this.messageService.warning('request.duplicate.reloadNeeded');
      return;
    }
    if ((this.beraterSkills ?? []).some(s => this.compareSkill(s, skill))) {
      this.messageService.info('request.duplicate.alreadySelected');
      return;
    }
    this.onSkillsSelected([...(this.beraterSkills ?? []), skill]);
  }
}
